import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_regular,
  font_family_weight_semibold,
  font_md,
  font_size_sm,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Mirrors `cardDetailTabSectionTitle` (the Statements/Activity tab kicker) from
 * the same design spec, exactly as `CardOverviewRecentTransactions.style.ts`
 * already does. Duplicated rather than imported because that style lives in
 * `src/page-components/Accounts/` and a feature must not depend on a page — this
 * is the third copy the EX-40630 plan sanctions over promoting it in the same
 * change.
 *
 * Uppercased via CSS, never in the translation source, so the accessible name
 * stays the plain phrase.
 */
export const title = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  margin: 0 0 ${spacing(4)};
`

export const rows = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};
  margin: 0;
`

/** One label/value pair, label left and value right, on a single baseline. */
export const row = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)};
`

export const rowLabel = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  margin: 0;
`

export const rowValue = css`
  ${font_md};
  color: ${text_colorTextPrimary};
  margin: 0;
  text-align: right;
  min-width: 0;
  overflow-wrap: anywhere;
`

/** Tabular numerals so masked digits, expiry and ZIP stay optically aligned. */
export const rowValueMono = css`
  ${rowValue};
  font-variant-numeric: tabular-nums;
`

/**
 * The "Cardholder & billing" block below the rows — a kicker, the name line(s),
 * then the address line. Not a `<dl>` row: it is free-form identity text, mapped
 * field by field per the design.
 */
export const block = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(1)};
  margin-top: ${spacing(5)};
  padding-top: ${spacing(5)};
  border-top: 1px solid ${base_colorContentBorder};
`

export const blockKicker = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

export const blockName = css`
  ${font_md};
  color: ${text_colorTextPrimary};
`

export const blockSub = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

/**
 * Shared frame for a group's loading / unavailable / error message, so a denied
 * or failed group keeps the panel's rhythm rather than collapsing to nothing.
 */
export const groupStatus = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  margin: ${spacing(2)} 0 0;
`

export const footer = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing(1)} ${spacing(3)};
  justify-content: space-between;
  margin-top: ${spacing(6)};
  padding-top: ${spacing(5)};
  border-top: 1px solid ${base_colorContentBorder};
`

export const footerText = css`
  ${font_sm};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`
